import { useCallback, useEffect, useState } from 'react';
import { ArrowClockwise, Check, CheckCircle, ClipboardText, Clock, Warning } from 'shared/icons';
import api from '../../services/api';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { DateTimeText, Id } from '../ui/Mono';
import Select from '../ui/Select';
import { Skeleton } from '../ui/Skeleton';
import Textarea from '../ui/Textarea';
import { useToast } from '../ui/Toast';
import { FormatLocationPlain } from './collector/collectorScan';

const STATUS_OPEN = 'OPEN';
const STATUS_RESOLVED = 'RESOLVED';

/**
 * UM campo de texto para as três coisas que se escreve num incidente (M4-1,
 * 11/08 — palavras do dono: "poderíamos ter apenas 1 textarea e um select de
 * duas opções … assim reduzimos a UI e consolidamos tudo").
 *
 * Antes eram dois textareas empilhados ("Adicionar anotação" e "O que foi
 * feito") — dois campos para o mesmo gesto (escrever o que aconteceu), e a
 * diferença entre eles era o BOTÃO que a pessoa apertava depois. Agora a
 * escolha vem ANTES, no seletor, e o campo é um só: o que muda é o rótulo, a
 * dica e o efeito do botão.
 *
 * `REOPEN` não é opção do seletor: quando o incidente está resolvido não há
 * escolha nenhuma a fazer — anotar é 409 e resolver já aconteceu. A única ação
 * possível vira a ação da seção, e o texto passa a ser a justificativa da
 * reabertura, que o backend EXIGE (M4-2).
 */
const ACTION_NOTE = 'NOTE';
const ACTION_RESOLVE = 'RESOLVE';
const ACTION_REOPEN = 'REOPEN';

// Rótulo/dica/placeholder por ação, num lugar só: a seção monta a partir daqui
// em vez de espalhar ternários por cinco elementos.
const ACTION_COPY = {
    [ACTION_NOTE]: {
        field: 'wms.incidents.add_note',
        placeholder: 'wms.incidents.add_note_placeholder',
        hint: 'wms.incidents.add_note_hint',
        submit: 'wms.incidents.add_note',
        // No SELETOR o texto é a escolha ("Adicionar como anotação" — palavras
        // do dono); no BOTÃO é a ação ("Adicionar anotação").
        option: 'wms.incidents.add_note_option',
    },
    [ACTION_RESOLVE]: {
        field: 'wms.incidents.resolve_notes',
        placeholder: 'wms.incidents.resolve_notes_placeholder',
        // Sem texto a dica é a EXIGÊNCIA; com texto, o que resolver significa.
        hint: 'wms.incidents.resolve_hint',
        emptyHint: 'wms.incidents.resolve_notes_required',
        submit: 'wms.incidents.resolve',
    },
    [ACTION_REOPEN]: {
        // O campo é a JUSTIFICATIVA, não o botão: reabrir sem explicar é 400 no
        // backend, e o rótulo tem que dizer isso antes de a pessoa digitar.
        field: 'wms.incidents.reopen_notes',
        placeholder: null,
        hint: 'wms.incidents.notes_closed',
        emptyHint: 'wms.incidents.reopen_notes_required',
        submit: 'wms.incidents.reopen',
    },
};

// As duas opções do seletor, na ordem em que o dono as escreveu.
const SELECTABLE_ACTIONS = [ACTION_NOTE, ACTION_RESOLVE];

/**
 * O endereço do incidente no shape que os utilitários de localização esperam
 * (`{code, type, rack, bay, level, position}`). O DTO do incidente traz as
 * mesmas colunas com o prefixo `location_` — `location_rack` é a LETRA do rack
 * e `location_block` o nº do bloco (vão), que aqui vira `bay`; converter em UM
 * lugar só evita que a página e este painel discordem sobre como falar o mesmo
 * endereço. `null` quando o incidente não tem posição (nem todo incidente tem).
 */
export function IncidentLocationOf(incident) {
    if (!incident?.location_code) {
        return null;
    }
    return {
        code: incident.location_code,
        type: incident.location_type,
        rack: incident.location_rack,
        bay: incident.location_block,
        level: incident.location_level,
        position: incident.location_position,
    };
}

/**
 * QUEM, numa regra só: a PESSOA (crachá) quando ela existe; a CONTA quando não.
 *
 * ⚠️ No chão a conta é COMPARTILHADA — o coletor loga como dispositivo, e a
 * pessoa se identifica pelo crachá. Mostrar "Coletor 01" como autor de tudo é o
 * mesmo que não registrar autor nenhum. Quem age do console não tem crachá na
 * jogada: ali a conta logada JÁ é a pessoa, e é ela que aparece.
 *
 * Uma função só, usada pela ficha E pela lista, para as duas telas nunca
 * discordarem sobre quem fez o quê. Devolve `{name, code}` — `code` é o crachá
 * (nulo quando a autoria veio da conta).
 */
export function IncidentPersonOf(source, { operatorField, accountField } = {}) {
    if (!source) {
        return null;
    }
    const operatorName = source[`${operatorField}_name`];
    if (operatorName) {
        return { name: operatorName, code: source[`${operatorField}_code`] ?? null };
    }
    const accountName = source[`${accountField}_name`] || source[accountField];
    return accountName ? { name: accountName, code: null } : null;
}

/** Quem REPORTOU o incidente (crachá do coletor ou conta do console). */
export function IncidentReporterOf(incident) {
    return IncidentPersonOf(incident, { operatorField: 'reported_by_operator', accountField: 'created_by' });
}

/** Quem escreveu UMA anotação. */
export function IncidentNoteAuthorOf(note) {
    return IncidentPersonOf(note, { operatorField: 'created_by_operator', accountField: 'created_by' });
}

/**
 * HISTÓRICO DO INCIDENTE — a ficha completa do que aconteceu (ajuste do aceite
 * do M4; palavras do dono: "quando começou, quem reportou, histórico de
 * anotações até dar como resolvido").
 *
 * ── Por que a linha do tempo tem três origens e uma leitura só ──────────────
 * A abertura e a resolução são COLUNAS do incidente (quem/quando + o relato,
 * quem/quando da resolução); as anotações do meio são LINHAS de
 * `wms_incident_notes`. Na tela isso vira uma coisa só, em ordem, porque é
 * assim que alguém audita: lendo de cima para baixo. O que a UI nunca faz é
 * misturar as duas naturezas — o relato de abertura é imutável e aparece
 * dentro do evento "Incidente aberto", nunca como uma nota qualquer.
 *
 * ── Por que resolver acontece AQUI, e não num modal só de resolver ──────────
 * Antes o botão Resolver abria um modal com um campo de texto e nada mais: dava
 * para encerrar um incidente sem ler uma linha do que já tinha sido registrado.
 * Resolver agora é a última seção DESTA tela — quem encerra passa,
 * obrigatoriamente, pelos olhos no histórico.
 *
 * ── Por que o rodapé só tem "Fechar" ───────────────────────────────────────
 * Resolver e Reabrir saíram dos botões do rodapé: as duas EXIGEM texto (400 no
 * backend sem ele), e botão longe do campo que ele exige é botão que só sabe
 * recusar. As duas ações moram ao lado do campo que as alimenta, na seção do
 * fim — que é também onde se lê o histórico antes de encerrar o caso.
 *
 * ── Append-only na interface, não só no banco ───────────────────────────────
 * Não existe editar nem apagar anotação: só o campo de acrescentar. Com o
 * incidente RESOLVED o campo continua na tela, mas a única ação que ele serve é
 * REABRIR (o histórico fecha na resolução; para registrar algo novo, reabra).
 * O backend recusa a nota com 409 de qualquer forma — a UI só não oferece o
 * caminho que seria negado.
 */
const IncidentDetailModal = ({
    incidentId,
    label,
    kindVariant = 'neutral',
    canManage = false,
    // `true` quando o Supervisor veio do botão Resolver da tabela: o seletor já
    // vem em "Resolver", com o histórico acima dele. Ele lê e escreve na mesma
    // tela — sem um segundo clique para chegar ao campo.
    startResolving = false,
    onClose,
    onChanged,
}) => {
    const toast = useToast();
    const [incident, setIncident] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    // UM rascunho para o campo único — e a ESCOLHA do seletor separada dele.
    const [draft, setDraft] = useState('');
    const [choice, setChoice] = useState(startResolving && canManage ? ACTION_RESOLVE : ACTION_NOTE);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!incidentId) {
            return;
        }
        setLoading(true);
        setLoadError(null);
        try {
            setIncident(await api.getWarehouseIncident(incidentId));
        } catch (error) {
            setLoadError(error?.message || label('wms.incidents.detail_error'));
            setIncident(null);
        } finally {
            setLoading(false);
        }
    }, [incidentId, label]);

    useEffect(() => {
        load();
    }, [load]);

    const isOpen = incident?.status === STATUS_OPEN;

    // A ação corrente: com o incidente ABERTO manda o seletor (e quem não
    // administra só tem uma opção, então não vê seletor nenhum); RESOLVIDO, a
    // única ação possível é reabrir.
    const action = isOpen ? (canManage ? choice : ACTION_NOTE) : ACTION_REOPEN;
    const copy = ACTION_COPY[action];
    const body = draft.trim();

    /**
     * UM envio para as três ações. O texto é obrigatório nas três — nas duas do
     * PATCH (resolver e reabrir) porque o backend devolve 400 sem ele (M4-1/
     * M4-2), e na anotação porque nota vazia não é nota.
     */
    const submit = async () => {
        if (!body) {
            return;
        }
        setBusy(true);
        try {
            if (action === ACTION_NOTE) {
                await api.createWarehouseIncidentNote(incidentId, body);
            } else {
                // `notes` no PATCH é a NOTA do histórico — não sobrescreve o
                // relato de abertura (contrato do M4). Resolvendo, é o que foi
                // feito; reabrindo, é a justificativa da reabertura.
                await api.updateWarehouseIncident(incidentId, {
                    status: action === ACTION_RESOLVE ? STATUS_RESOLVED : STATUS_OPEN,
                    notes: body,
                });
            }
            setDraft('');
            if (action === ACTION_REOPEN) {
                // Reaberto, o próximo gesto natural é anotar — não resolver de
                // novo o que se acabou de reabrir.
                setChoice(ACTION_NOTE);
            }
            toast.success(
                label(
                    action === ACTION_NOTE
                        ? 'wms.incidents.note_added_toast'
                        : action === ACTION_RESOLVE
                          ? 'wms.incidents.resolved_toast'
                          : 'wms.incidents.reopened_toast',
                ),
            );
            await load();
            onChanged?.();
        } catch (error) {
            toast.error(error);
        } finally {
            setBusy(false);
        }
    };

    const notes = Array.isArray(incident?.notes_history) ? incident.notes_history : [];
    const location = IncidentLocationOf(incident);
    const locationPlain = location ? FormatLocationPlain(location, { label }) : '';

    return (
        <Modal
            isOpen
            onClose={onClose}
            size="2xl"
            title={label('wms.incidents.detail_title')}
            icon={<Warning size={22} className="text-mono-700 dark:text-mono-300" />}
            actions={
                <Button variant="secondary" onClick={onClose} disabled={busy}>
                    {label('wms.common.close')}
                </Button>
            }
        >
            {loading ? (
                <Skeleton className="h-64 w-full" />
            ) : loadError ? (
                <p className="text-sm text-danger-600 dark:text-danger-400">{loadError}</p>
            ) : !incident ? null : (
                <div className="space-y-5">
                    {/* Cabeçalho da ficha: o QUE, ONDE e com QUAL carga */}
                    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg bg-mono-50 p-3 dark:bg-mono-900">
                        <div className="min-w-0 space-y-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                                <Badge variant={kindVariant}>{label(`wms.incident_kind.${incident.kind}`)}</Badge>
                                <Badge variant={isOpen ? 'warning' : 'success'}>
                                    {label(`wms.incident_status.${incident.status}`)}
                                </Badge>
                            </div>
                            <dl className="text-sm text-mono-600 dark:text-mono-400">
                                <div className="flex flex-wrap items-baseline gap-1.5">
                                    <dt>{label('wms.incidents.field.location')}:</dt>
                                    <dd>
                                        <Id value={incident.location_code} className="text-xs" />
                                        {locationPlain && locationPlain !== incident.location_code ? (
                                            <span className="ml-1.5 text-xs">{locationPlain}</span>
                                        ) : null}
                                    </dd>
                                </div>
                                <div className="flex flex-wrap items-baseline gap-1.5">
                                    <dt>{label('wms.incidents.field.lpn')}:</dt>
                                    <dd>
                                        <Id value={incident.lpn_code} className="text-xs" />
                                    </dd>
                                </div>
                            </dl>
                        </div>
                        <div className="space-y-1 text-right">
                            <p className="text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.incidents.field.notes_count')}: {notes.length}
                            </p>
                            {/* O id do incidente é o que se cita numa auditoria
                                ("o incidente 019fed30…") — sem ele, a conversa
                                depende de "aquele da R01-B06". */}
                            <Id value={incident.id} className="text-[11px] text-mono-500" />
                        </div>
                    </div>

                    <section>
                        <h3 className="mb-2 text-sm font-semibold text-black dark:text-white">
                            {label('wms.incidents.timeline_title')}
                        </h3>

                        <ol className="space-y-3 border-l border-mono-200 pl-4 dark:border-mono-800">
                            {/* 1. Abertura — quem reportou, quando e o que foi relatado */}
                            <TimelineEntry
                                icon={<Warning size={14} className="text-warning-600 dark:text-warning-400" />}
                                title={label('wms.incidents.timeline_opened')}
                                who={IncidentReporterOf(incident)}
                                when={incident.created_at}
                            >
                                {incident.notes ? (
                                    <p className="whitespace-pre-wrap text-sm text-mono-700 dark:text-mono-300">
                                        {incident.notes}
                                    </p>
                                ) : (
                                    <p className="text-sm italic text-mono-500 dark:text-mono-400">
                                        {label('wms.incidents.timeline_no_report')}
                                    </p>
                                )}
                            </TimelineEntry>

                            {/* 2. As anotações, na ordem em que foram escritas */}
                            {notes.length === 0 ? (
                                <li className="text-sm italic text-mono-500 dark:text-mono-400">
                                    {label('wms.incidents.timeline_empty')}
                                </li>
                            ) : (
                                notes.map((note) => (
                                    <TimelineEntry
                                        key={note.id}
                                        icon={<ClipboardText size={14} className="text-mono-500" />}
                                        title={label('wms.incidents.timeline_note')}
                                        who={IncidentNoteAuthorOf(note)}
                                        when={note.created_at}
                                    >
                                        <p className="whitespace-pre-wrap text-sm text-mono-700 dark:text-mono-300">
                                            {note.body}
                                        </p>
                                    </TimelineEntry>
                                ))
                            )}

                            {/* 3. O fim: resolvido (quem/quando) ou ainda aberto */}
                            {incident.status === STATUS_RESOLVED ? (
                                <TimelineEntry
                                    icon={<CheckCircle size={14} className="text-success-600 dark:text-success-400" />}
                                    title={label('wms.incidents.timeline_resolved')}
                                    who={incident.resolved_by_name ? { name: incident.resolved_by_name } : null}
                                    when={incident.resolved_at}
                                />
                            ) : (
                                <li className="flex items-center gap-2 text-sm text-mono-500 dark:text-mono-400">
                                    <Clock size={14} />
                                    {label('wms.incidents.timeline_pending')}
                                </li>
                            )}
                        </ol>
                    </section>

                    {/* UM campo, UM seletor (M4-1): escrever é sempre o mesmo
                        gesto; o que muda é o que se faz com o texto. Quem não
                        administra não vê seletor — só tem uma opção. */}
                    {isOpen || canManage ? (
                        <section className="space-y-2 rounded-lg border border-mono-200 p-3 dark:border-mono-800">
                            {isOpen && canManage ? (
                                <Select
                                    label={label('wms.incidents.action_label')}
                                    value={choice}
                                    onChange={(event) => setChoice(event.target.value)}
                                    disabled={busy}
                                >
                                    {SELECTABLE_ACTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {label(ACTION_COPY[option].option ?? ACTION_COPY[option].submit)}
                                        </option>
                                    ))}
                                </Select>
                            ) : null}
                            <Textarea
                                label={label(copy.field)}
                                rows={3}
                                value={draft}
                                onChange={(event) => setDraft(event.target.value)}
                                placeholder={copy.placeholder ? label(copy.placeholder) : undefined}
                                disabled={busy}
                                maxLength={2000}
                            />
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-xs text-mono-500 dark:text-mono-400">
                                    {label(!body && copy.emptyHint ? copy.emptyHint : copy.hint)}
                                </p>
                                <Button size="sm" onClick={submit} loading={busy} disabled={!body}>
                                    {action === ACTION_RESOLVE ? <Check size={14} className="mr-1.5" /> : null}
                                    {action === ACTION_REOPEN ? (
                                        <ArrowClockwise size={14} className="mr-1.5" />
                                    ) : null}
                                    {label(copy.submit)}
                                </Button>
                            </div>
                        </section>
                    ) : (
                        <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.incidents.notes_closed')}</p>
                    )}
                </div>
            )}
        </Modal>
    );
};

/**
 * Nome + crachá de quem fez. O crachá é código de máscara fixa → monoespaçado
 * (`Id`), a mesma regra que vale para LPN e endereço no resto do módulo.
 */
export const IncidentPersonText = ({ person, className = 'text-xs text-mono-500 dark:text-mono-400' }) => {
    if (!person?.name) {
        return null;
    }
    return (
        <span className={className}>
            {person.name}
            {person.code ? (
                <>
                    {' '}
                    <Id value={person.code} />
                </>
            ) : null}
        </span>
    );
};

/** Um evento da linha do tempo: ícone, quem, quando e o texto (opcional). */
const TimelineEntry = ({ icon, title, who = null, when, children = null }) => (
    <li className="relative">
        <span className="absolute -left-[22px] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white dark:bg-mono-950">
            {icon}
        </span>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-sm font-medium text-black dark:text-white">{title}</span>
            {who ? (
                <span className="text-xs text-mono-500 dark:text-mono-400">
                    — <IncidentPersonText person={who} className="" />
                </span>
            ) : null}
            <DateTimeText value={when} relative className="text-xs text-mono-500" />
        </div>
        {children}
    </li>
);

export default IncidentDetailModal;
